import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Wrench, MapPin, Calendar, DollarSign, FileText, Upload, CheckCircle, Clock, Loader } from 'lucide-react';
import api from '../../api/apiClient';

interface TimelineEvent {
  eventType: string;
  description: string;
  createdAt: string;
}

interface JobDetails {
  requestId: number;
  category: string;
  description: string;
  urgency: string;
  status: string;
  propertyAddress?: string;
  propertyType?: string;
  createdAt: string;
  quoteAmount?: number;
  quoteStatus?: string;
  timeline: TimelineEvent[];
}

export default function ContractorJobView() {
  const { token } = useParams<{ token: string }>();

  const [job, setJob] = useState<JobDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form states
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteAmount, setQuoteAmount] = useState('');
  const [quoteDescription, setQuoteDescription] = useState('');
  const [estimatedDuration, setEstimatedDuration] = useState('');

  const [showVisitForm, setShowVisitForm] = useState(false);
  const [visitDate, setVisitDate] = useState('');
  const [visitTime, setVisitTime] = useState('');
  const [visitNotes, setVisitNotes] = useState('');

  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [invoiceDescription, setInvoiceDescription] = useState('');
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (token) {
      loadJob();
    }
  }, [token]);

  const loadJob = async () => {
    try {
      const response = await api.get<JobDetails>(`/contractor/job/${token}`);
      setJob(response);
    } catch (err: any) {
      setError(err.message || 'Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitQuote = async () => {
    if (!quoteAmount || parseFloat(quoteAmount) <= 0) {
      alert('Please enter a valid quote amount');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/contractor/job/${token}/quote`, {
        amount: parseFloat(quoteAmount),
        description: quoteDescription,
        estimatedDuration
      });
      setSuccess('Quote submitted successfully!');
      setShowQuoteForm(false);
      loadJob();
    } catch (err: any) {
      alert('Failed: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleScheduleVisit = async () => {
    if (!visitDate) {
      alert('Please select a visit date');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/contractor/job/${token}/schedule-visit`, {
        visitDate,
        visitTime,
        notes: visitNotes
      });
      setSuccess('Visit scheduled successfully!');
      setShowVisitForm(false);
      loadJob();
    } catch (err: any) {
      alert('Failed: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadInvoice = async () => {
    if (!invoiceAmount || parseFloat(invoiceAmount) <= 0) {
      alert('Please enter a valid invoice amount');
      return;
    }

    if (!invoiceFile) {
      alert('Please select an invoice file');
      return;
    }

    setSubmitting(true);
    try {
      // In production, upload file to S3 first
      const invoiceUrl = `invoices/${Date.now()}_${invoiceFile.name}`;

      await api.post(`/contractor/job/${token}/invoice`, {
        amount: parseFloat(invoiceAmount),
        description: invoiceDescription,
        invoiceUrl
      });
      setSuccess('Invoice uploaded successfully! Awaiting approval.');
      setShowInvoiceForm(false);
      loadJob();
    } catch (err: any) {
      alert('Failed: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8F8F8' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#0C4432' }}></div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8F8F8' }}>
        <div className="text-center">
          <p className="text-xl font-semibold mb-2" style={{ color: '#DF6C4F' }}>
            {error || 'Job not found'}
          </p>
          <p style={{ color: '#666' }}>The link may be invalid or expired.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#F8F8F8' }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Wrench className="w-8 h-8" style={{ color: '#0C4432' }} />
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#0C4432' }}>Maintenance Job</h1>
            <p style={{ color: '#666' }}>Request #{job.requestId}</p>
          </div>
        </div>

        {success && (
          <div className="mb-6 p-4 rounded-md flex items-center gap-3" style={{ backgroundColor: '#E8F5F3' }}>
            <CheckCircle className="w-5 h-5" style={{ color: '#49c5b6' }} />
            <p style={{ color: '#0C4432' }}>{success}</p>
          </div>
        )}

        {/* Job Details Card */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#0C4432' }}>{job.category}</h2>
              <div className="flex items-center gap-2 mb-2">
                <span className="px-3 py-1 rounded-full text-sm font-medium"
                  style={{
                    backgroundColor: job.urgency === 'EMERGENCY' ? '#FEE' : '#F0F4F8',
                    color: job.urgency === 'EMERGENCY' ? '#DC2626' : '#0C4432'
                  }}>
                  {job.urgency}
                </span>
                <span className="px-3 py-1 rounded-full text-sm font-medium"
                  style={{ backgroundColor: '#E8F5F3', color: '#0C4432' }}>
                  {job.status}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {job.propertyAddress && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-1" style={{ color: '#666' }} />
                <div>
                  <p className="font-medium" style={{ color: '#222' }}>Property</p>
                  <p style={{ color: '#666' }}>{job.propertyAddress}</p>
                  {job.propertyType && <p className="text-sm" style={{ color: '#999' }}>{job.propertyType}</p>}
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 mt-1" style={{ color: '#666' }} />
              <div>
                <p className="font-medium" style={{ color: '#222' }}>Description</p>
                <p style={{ color: '#666' }}>{job.description}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 mt-1" style={{ color: '#666' }} />
              <div>
                <p className="font-medium" style={{ color: '#222' }}>Submitted</p>
                <p style={{ color: '#666' }}>{new Date(job.createdAt).toLocaleString()}</p>
              </div>
            </div>

            {job.quoteAmount && (
              <div className="p-4 rounded-md" style={{ backgroundColor: '#E8F5F3' }}>
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-5 h-5" style={{ color: '#0C4432' }} />
                  <p className="font-medium" style={{ color: '#222' }}>Quote</p>
                </div>
                <p className="text-2xl font-bold" style={{ color: '#0C4432' }}>
                  ${job.quoteAmount.toFixed(2)}
                </p>
                <p className="text-sm mt-1" style={{ color: '#666' }}>
                  Status: {job.quoteStatus?.replace(/_/g, ' ')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4" style={{ color: '#0C4432' }}>Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {!job.quoteAmount && (
              <button
                onClick={() => setShowQuoteForm(true)}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-md text-white font-medium"
                style={{ backgroundColor: '#0C4432' }}
              >
                <DollarSign className="w-4 h-4" />
                Submit Quote
              </button>
            )}

            <button
              onClick={() => setShowVisitForm(true)}
              className="flex items-center justify-center gap-2 px-4 py-3 border rounded-md font-medium hover:bg-gray-50"
              style={{ borderColor: '#49c5b6', color: '#49c5b6' }}
            >
              <Calendar className="w-4 h-4" />
              Schedule Visit
            </button>

            {job.quoteStatus === 'APPROVED' && (
              <button
                onClick={() => setShowInvoiceForm(true)}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-md text-white font-medium"
                style={{ backgroundColor: '#49c5b6' }}
              >
                <Upload className="w-4 h-4" />
                Upload Invoice
              </button>
            )}
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4" style={{ color: '#0C4432' }}>Timeline</h3>
          <div className="space-y-4">
            {job.timeline.map((event, index) => (
              <div key={index} className="flex gap-4">
                <div className="flex-shrink-0 w-2 h-2 rounded-full mt-2" style={{ backgroundColor: '#49c5b6' }}></div>
                <div className="flex-1">
                  <p className="font-medium" style={{ color: '#0C4432' }}>
                    {event.eventType.replace(/_/g, ' ')}
                  </p>
                  <p className="text-sm" style={{ color: '#666' }}>{event.description}</p>
                  <p className="text-xs mt-1" style={{ color: '#999' }}>
                    {new Date(event.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quote Form Modal */}
        {showQuoteForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#0C4432' }}>Submit Quote</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                    Quote Amount *
                  </label>
                  <input
                    type="number"
                    value={quoteAmount}
                    onChange={(e) => setQuoteAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border rounded-md"
                    step="0.01"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                    Description
                  </label>
                  <textarea
                    value={quoteDescription}
                    onChange={(e) => setQuoteDescription(e.target.value)}
                    placeholder="Describe the work to be done..."
                    className="w-full px-3 py-2 border rounded-md"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                    Estimated Duration
                  </label>
                  <input
                    type="text"
                    value={estimatedDuration}
                    onChange={(e) => setEstimatedDuration(e.target.value)}
                    placeholder="e.g., 2-3 hours"
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  onClick={handleSubmitQuote}
                  disabled={submitting}
                  className="flex-1 py-2 px-4 rounded-md text-white disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#0C4432' }}
                >
                  {submitting ? <Loader className="w-5 h-5 animate-spin" /> : 'Submit Quote'}
                </button>
                <button
                  onClick={() => setShowQuoteForm(false)}
                  className="px-4 py-2 border rounded-md"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Visit Form Modal */}
        {showVisitForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#0C4432' }}>Schedule Visit</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                    Visit Date *
                  </label>
                  <input
                    type="date"
                    value={visitDate}
                    onChange={(e) => setVisitDate(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                    Time
                  </label>
                  <input
                    type="time"
                    value={visitTime}
                    onChange={(e) => setVisitTime(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                    Notes
                  </label>
                  <textarea
                    value={visitNotes}
                    onChange={(e) => setVisitNotes(e.target.value)}
                    placeholder="Any special instructions..."
                    className="w-full px-3 py-2 border rounded-md"
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  onClick={handleScheduleVisit}
                  disabled={submitting}
                  className="flex-1 py-2 px-4 rounded-md text-white disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#49c5b6' }}
                >
                  {submitting ? <Loader className="w-5 h-5 animate-spin" /> : 'Schedule Visit'}
                </button>
                <button
                  onClick={() => setShowVisitForm(false)}
                  className="px-4 py-2 border rounded-md"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Invoice Form Modal */}
        {showInvoiceForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#0C4432' }}>Upload Invoice</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                    Invoice Amount *
                  </label>
                  <input
                    type="number"
                    value={invoiceAmount}
                    onChange={(e) => setInvoiceAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border rounded-md"
                    step="0.01"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                    Description
                  </label>
                  <textarea
                    value={invoiceDescription}
                    onChange={(e) => setInvoiceDescription(e.target.value)}
                    placeholder="Work completed..."
                    className="w-full px-3 py-2 border rounded-md"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                    Invoice File *
                  </label>
                  <input
                    type="file"
                    onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)}
                    accept=".pdf,.png,.jpg,.jpeg"
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                  <p className="text-xs mt-1" style={{ color: '#666' }}>
                    PDF, PNG, or JPG
                  </p>
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  onClick={handleUploadInvoice}
                  disabled={submitting}
                  className="flex-1 py-2 px-4 rounded-md text-white disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#49c5b6' }}
                >
                  {submitting ? <Loader className="w-5 h-5 animate-spin" /> : 'Upload Invoice'}
                </button>
                <button
                  onClick={() => setShowInvoiceForm(false)}
                  className="px-4 py-2 border rounded-md"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

